import { plateFont } from '../../utils/fonts';
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  Grid,
  MenuItem,
  Paper,
  Rating,
  Select,
  Snackbar,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import StarIcon from '@mui/icons-material/Star';
import PercentIcon from '@mui/icons-material/Percent';
import PhoneCallbackIcon from '@mui/icons-material/PhoneCallback';
import GoogleIcon from '@mui/icons-material/Google';
import FilterListOffIcon from '@mui/icons-material/FilterListOff';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFnsV3';
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Cell, Tooltip as RechartsTooltip } from 'recharts';
import { format } from 'date-fns';
import { fi } from 'date-fns/locale';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useUserStore } from 'store';

import DataTable from '../shared/DataTable';
import MetricCard from '../reports/components/MetricCard';
import { usePaginatedList } from '../../hooks/usePaginatedList';
import {
  useListPreferences,
  FilterButton,
  FilterPanel,
  FilterSection,
  SearchInput,
} from '../shared/ListFilters';
import FeedbackDetailDialog from './FeedbackDetailDialog';
import PaginationControls from '../shared/PaginationControls';

const STORAGE_KEY = 'customer_feedback_list_preferences';

const defaultPreferences = {
  sortField: 'submitted_at',
  sortDirection: 'desc',
  filters: {
    min_rating: '',
    max_rating: '',
    date_from: '',
    date_to: '',
    has_comment: '',
    followup_unactioned: '',
    customer_id: '',
  },
  searchQuery: '',
};

const STAR_COLORS = ['#ef4444', '#f97316', '#eab308', '#84cc16', '#22c55e'];

const dateStringToDate = (dateStr) => {
  if (!dateStr) return null;
  return new Date(dateStr + 'T00:00:00');
};

const dateToDateString = (date) => {
  if (!date || isNaN(date.getTime())) return '';
  const year = date.getFullYear();
  const month = (date.getMonth() + 1).toString().padStart(2, '0');
  const day = date.getDate().toString().padStart(2, '0');
  return `${year}-${month}-${day}`;
};

function truncateText(text, maxLen) {
  if (!text) return '';
  return text.length > maxLen ? text.substring(0, maxLen) + '...' : text;
}

export default function MainCustomerFeedbackView() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { currentOrganization } = useUserStore();
  const orgId = currentOrganization?.organization_id;

  // --- Stats ---
  const [stats, setStats] = useState(null);
  const [statsLoading, setStatsLoading] = useState(true);

  const fetchStats = useCallback(async () => {
    try {
      const response = await axios.get('/api/feedback-stats/', {
        params: { organization: orgId },
      });
      setStats(response.data);
    } catch (err) {
      console.error('Failed to load feedback stats:', err);
    } finally {
      setStatsLoading(false);
    }
  }, [orgId]);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  // --- List preferences ---
  const {
    preferences,
    updatePreferences,
    updateFilters,
    resetFilters,
    filtersOpen,
    toggleFiltersOpen,
  } = useListPreferences(STORAGE_KEY, defaultPreferences);

  // --- Paginated list ---
  const {
    data: responses,
    loading,
    page,
    pageSize,
    totalCount,
    totalPages,
    setPage,
    setPageSize,
    setFilters: setServerFilters,
    setSort,
    refetch,
  } = usePaginatedList('/api/feedback-responses/', {
    initialFilters: preferences.filters,
    defaultSortField: preferences.sortField,
    defaultSortDirection: preferences.sortDirection,
    initialSearchQuery: preferences.searchQuery || '',
  });

  // Sync preferences → server filters
  useEffect(() => {
    setServerFilters(preferences.filters);
  }, [preferences.filters, setServerFilters]);

  // --- Detail dialog ---
  const [selectedId, setSelectedId] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);

  const handleRowClick = (row) => {
    setSelectedId(row.id);
    setDialogOpen(true);
  };

  const handleFollowupActioned = () => {
    refetch();
    fetchStats();
  };

  // --- Active filter count ---
  const activeFilterCount = useMemo(() => {
    const f = preferences.filters;
    let count = 0;
    if (f.min_rating) count++;
    if (f.max_rating) count++;
    if (f.date_from) count++;
    if (f.date_to) count++;
    if (f.has_comment) count++;
    if (f.followup_unactioned) count++;
    if (f.customer_id) count++;
    if (preferences.searchQuery) count++;
    return count;
  }, [preferences]);

  // --- Rating distribution chart data ---
  const chartData = useMemo(() => {
    if (!stats?.rating_distribution) return [];
    return [1, 2, 3, 4, 5].map((star) => ({
      star: `${star}★`,
      count: stats.rating_distribution[star] || stats.rating_distribution[String(star)] || 0,
      fill: STAR_COLORS[star - 1],
    }));
  }, [stats]);

  // --- Table columns ---
  const columns = useMemo(() => [
    {
      key: 'date',
      label: t('customer_feedback.date'),
      width: '1%',
      noWrap: true,
      sortable: true,
      sortField: 'submitted_at',
      render: (row) => {
        try {
          return format(new Date(row.submitted_at), 'd.M.yyyy', { locale: fi });
        } catch {
          return '-';
        }
      },
    },
    {
      key: 'rating',
      label: t('customer_feedback.rating'),
      width: '1%',
      sortable: true,
      sortField: 'rating',
      render: (row) => (
        <Rating value={row.rating} readOnly size="small" />
      ),
    },
    {
      key: 'customer',
      label: t('customer_feedback.customer'),
      render: (row) => row.customer_name || '-',
    },
    {
      key: 'vehicle',
      label: t('customer_feedback.vehicle'),
      hideBelow: 'md',
      render: (row) => row.vehicle_registration || '-',
    },
    {
      key: 'work_number',
      label: t('customer_feedback.work_order'),
      width: '1%',
      hideBelow: 'lg',
      render: (row) => row.maintenance_work_number ? `#${row.maintenance_work_number}` : '-',
    },
    {
      key: 'comment',
      label: t('customer_feedback.comment'),
      maxWidth: 200,
      hideBelow: 'md',
      render: (row) => row.comment ? (
        <Tooltip title={row.comment} arrow>
          <span>{truncateText(row.comment, 60)}</span>
        </Tooltip>
      ) : (
        <Typography variant="body2" color="text.secondary">-</Typography>
      ),
    },
    {
      key: 'followup',
      label: t('customer_feedback.followup'),
      width: '1%',
      hideBelow: 'lg',
      render: (row) => {
        if (!row.low_rating_followup_requested) return '-';
        if (row.low_rating_followup_actioned) {
          return (
            <Box sx={{ display: 'inline-flex', alignItems: 'center', px: 1, py: 0.25, borderRadius: '12px', bgcolor: '#d1fae5', color: '#065f46', fontSize: '12px', fontWeight: 500 }}>
              {t('customer_feedback.followup_actioned')}
            </Box>
          );
        }
        return (
          <Box sx={{ display: 'inline-flex', alignItems: 'center', px: 1, py: 0.25, borderRadius: '12px', bgcolor: '#fef3c7', color: '#92400e', fontSize: '12px', fontWeight: 500 }}>
            {t('customer_feedback.followup_requested')}
          </Box>
        );
      },
    },
    {
      key: 'google',
      label: t('customer_feedback.google_column'),
      width: '1%',
      hideBelow: 'lg',
      render: (row) => row.google_cta_clicked ? (
        <Typography color="success.main" fontWeight={600}>✓</Typography>
      ) : '-',
    },
  ], [t]);

  // --- Check if feature is enabled ---
  const isEnabled = currentOrganization?.customer_feedback_enabled;

  return (
    <Box sx={{ p: { xs: 2, sm: 3 } }}>
      <Typography variant="h5" fontWeight={600} sx={{ mb: 3 }}>
        {t('customer_feedback.title')}
      </Typography>

      {/* KPI Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard
            icon={PercentIcon}
            label={t('customer_feedback.response_rate')}
            value={stats?.response_rate_30d}
            format="percent"
            loading={statsLoading}
            iconColor="primary.main"
            iconBgColor="rgba(26, 54, 93, 0.1)"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard
            icon={StarIcon}
            label={t('customer_feedback.average_rating')}
            value={stats?.average_rating_30d}
            format="decimal"
            loading={statsLoading}
            iconColor="#eab308"
            iconBgColor="rgba(234, 179, 8, 0.1)"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard
            icon={PhoneCallbackIcon}
            label={t('customer_feedback.pending_followups')}
            value={stats?.pending_low_rating_followups_count}
            format="number"
            loading={statsLoading}
            iconColor={(stats?.pending_low_rating_followups_count || 0) > 0 ? 'error.main' : 'text.secondary'}
            iconBgColor={(stats?.pending_low_rating_followups_count || 0) > 0 ? 'rgba(239, 68, 68, 0.1)' : 'rgba(100, 116, 139, 0.1)'}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard
            icon={GoogleIcon}
            label={t('customer_feedback.google_click_rate')}
            value={stats?.google_cta_click_rate}
            format="percent"
            loading={statsLoading}
            iconColor="#4285f4"
            iconBgColor="rgba(66, 133, 244, 0.1)"
          />
        </Grid>
      </Grid>

      {/* Rating Distribution Chart */}
      {chartData.length > 0 && chartData.some((d) => d.count > 0) && (
        <Paper elevation={0} sx={{ p: 2, mb: 3 }}>
          <Typography variant="subtitle2" fontWeight={600} color="text.secondary" sx={{ mb: 1 }}>
            {t('customer_feedback.rating_distribution')}
          </Typography>
          <ResponsiveContainer width="100%" height={120}>
            <BarChart data={chartData} layout="vertical" margin={{ top: 0, right: 20, left: 10, bottom: 0 }}>
              <XAxis type="number" hide />
              <YAxis type="category" dataKey="star" width={30} tick={{ fontSize: 13 }} />
              <RechartsTooltip formatter={(value) => [value, t('customer_feedback.rating')]} />
              <Bar dataKey="count" radius={[0, 4, 4, 0]} barSize={16}>
                {chartData.map((entry, index) => (
                  <Cell key={index} fill={entry.fill} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </Paper>
      )}

      {/* Search & Filters */}
      <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
        <Box sx={{ flex: 1, minWidth: 200 }}>
          <SearchInput
            value={preferences.searchQuery}
            onChange={(val) => updatePreferences({ searchQuery: val })}
            onClear={() => updatePreferences({ searchQuery: '' })}
            placeholder={t('customer_feedback.search_placeholder')}
          />
        </Box>
        <FilterButton
          t={t}
          activeCount={activeFilterCount}
          isOpen={filtersOpen}
          onClick={toggleFiltersOpen}
        />
        {activeFilterCount > 0 && (
          <Button
            size="small"
            startIcon={<FilterListOffIcon />}
            onClick={() => {
              resetFilters();
              updatePreferences({ searchQuery: '' });
            }}
            sx={{ textTransform: 'none' }}
          >
            {t('customer_feedback.clear_filters')}
          </Button>
        )}
      </Box>

      {filtersOpen && (
        <FilterPanel
          open={filtersOpen}
          t={t}
          resultCount={totalCount}
          onReset={resetFilters}
        >
          <Grid container spacing={2}>
            <Grid item xs={6} sm={3}>
              <TextField
                select
                label={t('customer_feedback.filter_min_rating')}
                size="small"
                fullWidth
                value={preferences.filters.min_rating || ''}
                onChange={(e) => updateFilters({ min_rating: e.target.value })}
              >
                <MenuItem value="">-</MenuItem>
                {[1, 2, 3, 4, 5].map((n) => (
                  <MenuItem key={n} value={String(n)}>{n} ★</MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={6} sm={3}>
              <TextField
                select
                label={t('customer_feedback.filter_max_rating')}
                size="small"
                fullWidth
                value={preferences.filters.max_rating || ''}
                onChange={(e) => updateFilters({ max_rating: e.target.value })}
              >
                <MenuItem value="">-</MenuItem>
                {[1, 2, 3, 4, 5].map((n) => (
                  <MenuItem key={n} value={String(n)}>{n} ★</MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={6} sm={3}>
              <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={fi}>
                <DatePicker
                  label={t('customer_feedback.filter_date_from')}
                  value={dateStringToDate(preferences.filters.date_from)}
                  onChange={(newValue) => updateFilters({ date_from: dateToDateString(newValue) })}
                  format="dd.MM.yyyy"
                  slotProps={{
                    textField: { size: 'small', fullWidth: true },
                    field: { clearable: true },
                  }}
                />
              </LocalizationProvider>
            </Grid>
            <Grid item xs={6} sm={3}>
              <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={fi}>
                <DatePicker
                  label={t('customer_feedback.filter_date_to')}
                  value={dateStringToDate(preferences.filters.date_to)}
                  onChange={(newValue) => updateFilters({ date_to: dateToDateString(newValue) })}
                  format="dd.MM.yyyy"
                  slotProps={{
                    textField: { size: 'small', fullWidth: true },
                    field: { clearable: true },
                  }}
                />
              </LocalizationProvider>
            </Grid>
            <Grid item xs={6} sm={3}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={preferences.filters.has_comment === 'true'}
                    onChange={(e) => updateFilters({ has_comment: e.target.checked ? 'true' : '' })}
                    size="small"
                  />
                }
                label={t('customer_feedback.filter_has_comment')}
              />
            </Grid>
            <Grid item xs={6} sm={3}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={preferences.filters.followup_unactioned === 'true'}
                    onChange={(e) => updateFilters({ followup_unactioned: e.target.checked ? 'true' : '' })}
                    size="small"
                  />
                }
                label={t('customer_feedback.filter_followup_unactioned')}
              />
            </Grid>
          </Grid>
        </FilterPanel>
      )}

      {/* Mobile Cards (xs–lg) */}
      <Box sx={{ display: { xs: 'block', lg: 'none' }, position: 'relative' }}>
        {loading && responses.length > 0 && (
          <Box sx={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, bgcolor: 'rgba(255,255,255,0.7)', zIndex: 1, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', pt: 8 }}>
            <CircularProgress size={32} />
          </Box>
        )}
        {loading && responses.length === 0 ? (
          <Box sx={{ p: 6, display: 'flex', justifyContent: 'center' }}>
            <CircularProgress size={32} />
          </Box>
        ) : responses.length === 0 ? (
          <Paper elevation={0} sx={{ p: 4, textAlign: 'center' }}>
            <Typography color="text.secondary">
              {t('customer_feedback.no_feedback_yet')}
            </Typography>
          </Paper>
        ) : (
          <>
            {responses.map((row) => (
              <Paper
                key={row.id}
                elevation={0}
                onClick={() => handleRowClick(row)}
                sx={{ p: 2, mb: 1.5, cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' } }}
              >
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography sx={{ fontSize: '14px', fontWeight: 600, mb: 0.25 }}>
                      {row.customer_name || '-'}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                      {row.vehicle_registration && (
                        <Typography sx={{ fontSize: '12px', ...plateFont, color: 'text.secondary' }}>
                          {row.vehicle_registration}
                        </Typography>
                      )}
                      {row.maintenance_work_number && (
                        <Typography sx={{ fontSize: '12px', color: 'text.secondary' }}>
                          #{row.maintenance_work_number}
                        </Typography>
                      )}
                      <Typography sx={{ fontSize: '12px', color: 'text.disabled' }}>
                        {(() => { try { return format(new Date(row.submitted_at), 'd.M.yyyy', { locale: fi }); } catch { return '-'; } })()}
                      </Typography>
                    </Box>
                  </Box>
                  <Rating value={row.rating} readOnly size="small" sx={{ flexShrink: 0 }} />
                </Box>
                {row.comment && (
                  <Typography sx={{ fontSize: '13px', color: 'text.secondary', mt: 0.5 }} noWrap>
                    {row.comment}
                  </Typography>
                )}
                {row.low_rating_followup_requested && (
                  <Box sx={{ mt: 1 }}>
                    <Box sx={{
                      display: 'inline-flex', px: 1, py: 0.25, borderRadius: '12px', fontSize: '11px', fontWeight: 500,
                      ...(row.low_rating_followup_actioned
                        ? { bgcolor: '#d1fae5', color: '#065f46' }
                        : { bgcolor: '#fef3c7', color: '#92400e' }),
                    }}>
                      {row.low_rating_followup_actioned ? t('customer_feedback.followup_actioned') : t('customer_feedback.followup_requested')}
                    </Box>
                  </Box>
                )}
              </Paper>
            ))}
            <PaginationControls
              page={page}
              totalPages={totalPages}
              totalCount={totalCount}
              pageSize={pageSize}
              onPageChange={setPage}
              onPageSizeChange={setPageSize}
              loading={loading}
            />
          </>
        )}
      </Box>

      {/* Data Table (lg+) */}
      <DataTable
        columns={columns}
        data={responses}
        loading={loading}
        onRowClick={handleRowClick}
        sortField={preferences.sortField}
        sortDirection={preferences.sortDirection}
        onSort={(field) => {
          const newDir = preferences.sortField === field && preferences.sortDirection === 'asc' ? 'desc' : 'asc';
          updatePreferences({ sortField: field, sortDirection: newDir });
          setSort(field, newDir);
        }}
        pagination={{
          page,
          pageSize,
          totalCount,
          totalPages,
          onPageChange: setPage,
          onPageSizeChange: setPageSize,
        }}
        emptyState={
          <Box sx={{ textAlign: 'center', py: 6 }}>
            <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
              {t('customer_feedback.no_feedback_yet')}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {t('customer_feedback.no_feedback_hint')}
            </Typography>
            {!isEnabled && (
              <Button
                variant="outlined"
                size="small"
                onClick={() => navigate('/settings?tab=feedback')}
                sx={{ textTransform: 'none' }}
              >
                {t('customer_feedback.enable_in_settings')}
              </Button>
            )}
          </Box>
        }
      />

      {/* Detail Dialog */}
      <FeedbackDetailDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        feedbackId={selectedId}
        onFollowupActioned={handleFollowupActioned}
      />
    </Box>
  );
}
